import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, TextField, Button, Alert, Divider, Chip, Grid } from '@mui/material';
import ThemeSettings from './ThemeSettings';
import { updateTrialAll, changeCredentials, getApiKeys, saveApiKey, sendBroadcast } from '../api';

function TwoFactorSetup() {
  const [qr, setQr] = React.useState(null);
  const [secret, setSecret] = React.useState('');
  const [code, setCode] = React.useState('');
  const [enabled, setEnabled] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const tok = localStorage.getItem('dashboard_token');

  React.useEffect(() => {
    fetch('/api/2fa/setup', { headers:{ Authorization:'Bearer '+tok } })
      .then(r=>r.json()).then(d=>{ setQr(d.qr); setSecret(d.secret); });
    fetch('/api/api-keys', { headers:{ Authorization:'Bearer '+tok } })
      .then(r=>r.json()).then(keys=>{ if(keys.find&&keys.find(k=>k.service==='2fa_enabled')) setEnabled(true); }).catch(()=>{});
  }, []);

  const enable = async () => {
    const r = await fetch('/api/2fa/enable?code='+code, { method:'POST', headers:{ Authorization:'Bearer '+tok } });
    if(r.ok){ setEnabled(true); setMsg('✅ 2FA увімкнено!'); }
    else setMsg('❌ Невірний код');
    setTimeout(()=>setMsg(''),3000);
  };

  const disable = async () => {
    if(!window.confirm('Вимкнути 2FA?')) return;
    await fetch('/api/2fa/disable', { method:'POST', headers:{ Authorization:'Bearer '+tok } });
    setEnabled(false); setMsg('✅ 2FA вимкнено');
    setTimeout(()=>setMsg(''),3000);
  };

  return (
    <Box>
      {enabled ? (
        <Box>
          <Typography sx={{ color:'#4caf50', mb:1 }}>✅ 2FA увімкнено</Typography>
          <Button variant="outlined" onClick={disable} sx={{ borderColor:'#f44', color:'#f44', mr:1 }}>Вимкнути 2FA</Button>
          <Button variant="outlined" onClick={()=>setEnabled(false)} sx={{ borderColor:'var(--ac1, #7c4dff)', color:'var(--ac1, #7c4dff)' }}>Додати пристрій</Button>
        </Box>
      ) : (
        <Box>
          <Typography variant="body2" sx={{ color:'#aaa', mb:2 }}>Відскануй QR код в Google Authenticator:</Typography>
          {qr && <img src={'data:image/png;base64,'+qr} alt="QR" style={{ width:200, height:200, display:'block', marginBottom:16 }}/>}
          <Typography variant="caption" sx={{ color:'#555', display:'block', mb:2 }}>Або введи секрет вручну: <b style={{color:'var(--ac1, #7c4dff)'}}>{secret}</b></Typography>
          <Box sx={{ display:'flex', gap:1 }}>
            <TextField size="small" placeholder="Код з додатку" value={code} onChange={e=>setCode(e.target.value)}
              inputProps={{ maxLength:6 }}
              sx={{ width:160, input:{color:'#fff',letterSpacing:4}, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#555'} }}/>
            <Button variant="contained" onClick={enable} sx={{ bgcolor:'var(--ac1, #7c4dff)' }}>Увімкнути</Button>
          </Box>
          {msg && <Typography variant="caption" sx={{ color: msg.includes('✅')?'#4caf50':'#f44', display:'block', mt:1 }}>{msg}</Typography>}
        </Box>
      )}
    </Box>
  );
}

function MaxPerSession() {
  const [val, setVal] = React.useState(5);
  const [msg, setMsg] = React.useState('');
  const tok = localStorage.getItem('dashboard_token');

  React.useEffect(() => {
    fetch('/api/settings/max-per-session', { headers:{ Authorization:'Bearer '+tok } })
      .then(r=>r.json()).then(d=>setVal(d.value));
  }, []);

  const save = async () => {
    const r = await fetch('/api/settings/max-per-session?value='+val,
      { method:'POST', headers:{ Authorization:'Bearer '+tok } });
    if(r.ok){ setMsg('✅ Збережено!'); setTimeout(()=>setMsg(''),3000); }
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ color:'#aaa', mb:1 }}>
        Максимум версій за один раз (для всіх юзерів):
      </Typography>
      <Box sx={{ display:'flex', gap:1, alignItems:'center' }}>
        <TextField size="small" type="number" value={val}
          onChange={e=>setVal(parseInt(e.target.value)||1)}
          inputProps={{ min:1, max:50, style:{color:'#fff', width:60} }}
          sx={{ '& .MuiOutlinedInput-notchedOutline':{borderColor:'#555'} }}/>
        <Button variant="contained" onClick={save} sx={{ bgcolor:'var(--ac1, #7c4dff)' }}>Зберегти</Button>
        {msg && <Typography variant="caption" sx={{ color:'#4caf50' }}>{msg}</Typography>}
      </Box>
    </Box>
  );
}

function ShowUniqInfo() {
  const [enabled, setEnabled] = React.useState(true);
  const [msg, setMsg] = React.useState('');
  const tok = localStorage.getItem('dashboard_token');
  React.useEffect(() => {
    fetch('/api/settings/show-uniq-info', { headers:{ Authorization:'Bearer '+tok } })
      .then(r=>r.json()).then(d=>setEnabled(d.enabled));
  }, []);
  const save = async (val) => {
    setEnabled(val);
    const r = await fetch(`/api/settings/show-uniq-info?enabled=${val}`,
      { method:'POST', headers:{ Authorization:'Bearer '+tok } });
    if(r.ok){ setMsg(val ? '✅ Показано' : '✅ Приховано'); setTimeout(()=>setMsg(''),3000); }
  };
  return (
    <Box sx={{ display:'flex', alignItems:'center', gap:2 }}>
      <Typography variant="body2" sx={{ color:'#aaa' }}>
        {enabled ? '👁 Блок унікалізації показується всім' : '🙈 Блок унікалізації прихований для всіх'}
      </Typography>
      <Button variant="contained" onClick={()=>save(!enabled)}
        sx={{ bgcolor: enabled ? '#4caf50' : '#f44336', minWidth:120 }}>
        {enabled ? 'Приховати' : 'Показати'}
      </Button>
      {msg && <Typography variant="caption" sx={{ color:'#4caf50' }}>{msg}</Typography>}
    </Box>
  );
}

function MicroCutsToggle() {
  // [D-316] Глобальний вимикач мікро-вирізок (D-315).
  // Ключ referral_settings.uniq_cuts_enabled. Поки false — команда /cuts у боті
  // мовчить, секція в редакторі не показується, поведінка обробки не змінюється.
  const [on, setOn] = React.useState(false);
  const [busy, setBusy] = React.useState(true);
  const [msg, setMsg] = React.useState('');
  const tok = localStorage.getItem('dashboard_token');
  React.useEffect(() => {
    fetch('/api/referral/settings', { headers:{ Authorization:'Bearer '+tok } })
      .then(r=>r.json())
      .then(d=>{ setOn(String(d.uniq_cuts_enabled).toLowerCase()==='true'); setBusy(false); })
      .catch(()=>setBusy(false));
  }, []);
  const save = async (val) => {
    setBusy(true);
    const r = await fetch('/api/referral/settings', {
      method:'POST',
      headers:{ Authorization:'Bearer '+tok, 'Content-Type':'application/json' },
      body: JSON.stringify({ uniq_cuts_enabled: val ? 'true' : 'false' })
    });
    if(r.ok){ setOn(val); setMsg(val ? '✅ Увімкнено' : '✅ Вимкнено'); setTimeout(()=>setMsg(''),3000); }
    else { setMsg('❌ Не вдалось зберегти'); setTimeout(()=>setMsg(''),4000); }
    setBusy(false);
  };
  return (
    <Box sx={{ mt:2, pt:2, borderTop:'1px solid #333' }}>
      <Typography variant="body2" sx={{ color:'#fff', mb:0.5 }}>✂️ Мікро-вирізки</Typography>
      <Typography variant="caption" sx={{ color:'#888', display:'block', mb:1.5 }}>
        Бот вирізає 1–3 коротких фрагменти (0.3–0.8с) на межах сцен — у кожної версії
        в різних місцях. Перші 3 секунди не чіпаються. Відео «одним планом» (без змін
        сцени) лишається без вирізок.
      </Typography>
      <Box sx={{ display:'flex', alignItems:'center', gap:2, flexWrap:'wrap' }}>
        <Typography variant="body2" sx={{ color:'#aaa' }}>
          {on ? '✂️ Доступно юзерам (кнопка /cuts у боті)' : '⛔ Вимкнено — юзери не бачать функції'}
        </Typography>
        <Button variant="contained" disabled={busy} onClick={()=>save(!on)}
          sx={{ bgcolor: on ? '#f44336' : '#4caf50', minWidth:120 }}>
          {on ? 'Вимкнути' : 'Увімкнути'}
        </Button>
        {msg && <Typography variant="caption" sx={{ color: msg.startsWith('❌') ? '#f44336' : '#4caf50' }}>{msg}</Typography>}
      </Box>
    </Box>
  );
}

function FileLimits() {
  const [sizeMb, setSizeMb] = React.useState(500);
  const [durSec, setDurSec] = React.useState(600);
  const [msg, setMsg] = React.useState('');
  const tok = localStorage.getItem('dashboard_token');
  React.useEffect(() => {
    fetch('/api/settings/file-limits', { headers:{ Authorization:'Bearer '+tok } })
      .then(r=>r.json()).then(d=>{ setSizeMb(d.max_file_size_mb); setDurSec(d.max_duration_sec); });
  }, []);
  const save = async () => {
    const r = await fetch(`/api/settings/file-limits?max_file_size_mb=${sizeMb}&max_duration_sec=${durSec}`,
      { method:'POST', headers:{ Authorization:'Bearer '+tok } });
    if(r.ok){ setMsg('✅ Збережено!'); setTimeout(()=>setMsg(''),3000); }
  };
  return (
    <Box>
      <Typography variant="body2" sx={{ color:'#aaa', mb:2 }}>Для всіх юзерів (0 = без обмежень):</Typography>
      <Box sx={{ display:'flex', gap:2, flexWrap:'wrap', alignItems:'center', mb:1 }}>
        <Box>
          <Typography variant="caption" sx={{ color:'#aaa' }}>📦 Макс. розмір файлу (МБ)</Typography>
          <TextField size="small" type="number" value={sizeMb} onChange={e=>setSizeMb(parseInt(e.target.value)||0)}
            inputProps={{ min:0, max:2000, style:{color:'#fff', width:80} }}
            sx={{ display:'block', mt:0.5, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#555'} }}/>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color:'#aaa' }}>⏱ Макс. тривалість (сек)</Typography>
          <TextField size="small" type="number" value={durSec} onChange={e=>setDurSec(parseInt(e.target.value)||0)}
            inputProps={{ min:0, max:7200, style:{color:'#fff', width:80} }}
            sx={{ display:'block', mt:0.5, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#555'} }}/>
        </Box>
        <Box sx={{ alignSelf:'flex-end' }}>
          <Button variant="contained" onClick={save} sx={{ bgcolor:'#ff9800' }}>Зберегти</Button>
        </Box>
      </Box>
      {msg && <Typography variant="caption" sx={{ color:'#4caf50' }}>{msg}</Typography>}
    </Box>
  );
}

function AuxFileLimits() {
  const [adMb, setAdMb] = React.useState(300);
  const [adSec, setAdSec] = React.useState(60);
  const [ckMb, setCkMb] = React.useState(300);
  const [ckSec, setCkSec] = React.useState(30);
  const [ckCount, setCkCount] = React.useState(5);
  const [ovCount, setOvCount] = React.useState(5);
  const [ovMb, setOvMb] = React.useState(300);
  const [ovSec, setOvSec] = React.useState(30);
  const [vol, setVol] = React.useState(null);
  const [msg, setMsg] = React.useState('');
  const tok = localStorage.getItem('dashboard_token');
  React.useEffect(() => {
    fetch('/api/settings/aux-limits', { headers:{ Authorization:'Bearer '+tok } })
      .then(r=>r.json()).then(d=>{ setAdMb(d.max_ad_size_mb); setAdSec(d.max_ad_duration_sec); setCkMb(d.max_ck_size_mb); setCkSec(d.max_ck_duration_sec); setCkCount(d.max_ck_count); setOvCount(d.max_overlay_count); setOvMb(d.max_overlay_size_mb); setOvSec(d.max_overlay_duration_sec); });
    fetch('/api/settings/aux-volume', { headers:{ Authorization:'Bearer '+tok } })
      .then(r=>r.json()).then(d=>setVol(d)).catch(()=>{});
  }, []);
  const save = async () => {
    const r = await fetch(`/api/settings/aux-limits?max_ad_size_mb=${adMb}&max_ad_duration_sec=${adSec}&max_ck_size_mb=${ckMb}&max_ck_duration_sec=${ckSec}&max_ck_count=${ckCount}&max_overlay_count=${ovCount}&max_overlay_size_mb=${ovMb}&max_overlay_duration_sec=${ovSec}`,
      { method:'POST', headers:{ Authorization:'Bearer '+tok } });
    if(r.ok){ setMsg('✅ Збережено!'); setTimeout(()=>setMsg(''),3000); }
  };
  const numBox = (label, val, setter, max) => (
    <Box>
      <Typography variant="caption" sx={{ color:'#aaa' }}>{label}</Typography>
      <TextField size="small" type="number" value={val} onChange={e=>setter(parseInt(e.target.value)||0)}
        inputProps={{ min:0, max, style:{color:'#fff', width:80} }}
        sx={{ display:'block', mt:0.5, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#555'} }}/>
    </Box>
  );
  return (
    <Box>
      <Typography variant="body2" sx={{ color:'#aaa', mb:1 }}>Для всіх юзерів (0 = без обмежень):</Typography>
      <Typography variant="caption" sx={{ color:'#ff9800', display:'block', mb:0.5 }}>📢 Реклама</Typography>
      <Box sx={{ display:'flex', gap:2, flexWrap:'wrap', alignItems:'center', mb:1.5 }}>
        {numBox('📦 Розмір (МБ)', adMb, setAdMb, 2000)}
        {numBox('⏱ Тривалість (сек)', adSec, setAdSec, 3600)}
      </Box>
      <Typography variant="caption" sx={{ color:'#4caf50', display:'block', mb:0.5 }}>🟩 Хромакеї</Typography>
      <Box sx={{ display:'flex', gap:2, flexWrap:'wrap', alignItems:'center', mb:1.5 }}>
        {numBox('📦 Розмір (МБ)', ckMb, setCkMb, 2000)}
        {numBox('⏱ Тривалість (сек)', ckSec, setCkSec, 3600)}
        <Box>
          <Typography variant="caption" sx={{ color:'#aaa' }}>🔢 Кількість (1-20)</Typography>
          <TextField size="small" type="number" value={ckCount} onChange={e=>setCkCount(Math.min(20, Math.max(1, parseInt(e.target.value)||1)))}
            inputProps={{ min:1, max:20, style:{color:'#fff', width:80} }}
            sx={{ display:'block', mt:0.5, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#555'} }}/>
        </Box>
      </Box>
      <Typography variant="caption" sx={{ color:'#2196f3', display:'block', mb:0.5 }}>🖼 Оверлеї</Typography>
      <Box sx={{ display:'flex', gap:2, flexWrap:'wrap', alignItems:'center', mb:1 }}>
        {numBox('📦 Розмір (МБ)', ovMb, setOvMb, 2000)}
        {numBox('⏱ Тривалість (сек)', ovSec, setOvSec, 3600)}
        <Box>
          <Typography variant="caption" sx={{ color:'#aaa' }}>🔢 Кількість (1-20)</Typography>
          <TextField size="small" type="number" value={ovCount} onChange={e=>setOvCount(Math.min(20, Math.max(1, parseInt(e.target.value)||1)))}
            inputProps={{ min:1, max:20, style:{color:'#fff', width:80} }}
            sx={{ display:'block', mt:0.5, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#555'} }}/>
        </Box>
        <Box sx={{ alignSelf:'flex-end' }}>
          <Button variant="contained" onClick={save} sx={{ bgcolor:'#ff9800' }}>Зберегти</Button>
        </Box>
      </Box>
      {msg && <Typography variant="caption" sx={{ color:'#4caf50' }}>{msg}</Typography>}
      <Box sx={{ mt:1.5, pt:1.5, borderTop:'1px solid #444' }}>
        <Typography variant="caption" sx={{ color:'#aaa', display:'block', mb:0.5 }}>📊 Зайнятий об'єм (усі юзери):</Typography>
        {vol ? (
          <Typography variant="body2" sx={{ color:'#fff' }}>
            📢 Реклами: <b>{vol.ads_total_mb} МБ</b> ({vol.ads_count} шт) · 🟩 Хромакеї: <b>{vol.ck_total_mb} МБ</b> ({vol.ck_count} шт)
          </Typography>
        ) : (
          <Typography variant="caption" sx={{ color:'#777' }}>Завантаження…</Typography>
        )}
      </Box>
    </Box>
  );
}

function MenuOpacity() {
  const [opacity, setOpacity] = React.useState(() => parseFloat(localStorage.getItem('menu_opacity') || '1'));

  const handleChange = (e) => {
    const val = parseFloat(e.target.value);
    setOpacity(val);
    localStorage.setItem('menu_opacity', val.toString());
    localStorage.setItem('card_opacity', val.toString());
    window.dispatchEvent(new Event('menu_opacity_changed'));
    window.dispatchEvent(new Event('card_opacity_changed'));
  };

  return (
    <Box>
      <Box sx={{ display:'flex', alignItems:'center', gap:2 }}>
        <Typography variant="body2" sx={{ color:'#aaa', minWidth:80 }}>Прозорість: {Math.round(opacity*100)}%</Typography>
        <input type="range" min="0.1" max="1" step="0.05" value={opacity} onChange={handleChange}
          style={{ flexGrow:1, accentColor:'var(--ac1, #7c4dff)' }}/>
      </Box>
      <Box sx={{ mt:1, p:1.5, bgcolor:`rgba(26,26,46,${opacity})`, border:'1px solid #333', borderRadius:1 }}>
        <Typography variant="caption" sx={{ color:'#aaa' }}>Попередній перегляд меню</Typography>
      </Box>
    </Box>
  );
}

function BgUploader() {
  const [bgSize, setBgSize] = React.useState(() => localStorage.getItem('bg_size') || 'cover');
  const [bgPosX, setBgPosX] = React.useState(() => parseInt(localStorage.getItem('bg_pos_x') || '50'));
  const [bgPosY, setBgPosY] = React.useState(() => parseInt(localStorage.getItem('bg_pos_y') || '50'));

  const applyBg = (size, x, y) => {
    localStorage.setItem('bg_size', size);
    localStorage.setItem('bg_pos_x', x);
    localStorage.setItem('bg_pos_y', y);
    window.dispatchEvent(new Event('bg_settings_changed'));
  };

  const handleSize = (val) => { setBgSize(val); applyBg(val, bgPosX, bgPosY); };
  const handleX = (val) => { setBgPosX(val); applyBg(bgSize, val, bgPosY); };
  const handleY = (val) => { setBgPosY(val); applyBg(bgSize, bgPosX, val); };
  const [bg, setBg] = React.useState(null);
  const [msg, setMsg] = React.useState('');
  const tok = localStorage.getItem('dashboard_token');

  React.useEffect(() => {
    fetch('/api/settings/background', { headers: { Authorization: 'Bearer ' + tok } })
      .then(r => r.json()).then(d => { if (d.path) setBg(d.path + '?t=' + Date.now()); });
  }, []);

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    const r = await fetch('/api/settings/background', { method: 'POST', headers: { Authorization: 'Bearer ' + tok }, body: fd });
    const d = await r.json();
    setBg(d.path + '?t=' + Date.now());
    setMsg('✅ Збережено! Оновіть сторінку.');
    setTimeout(() => setMsg(''), 4000);
  };

  const del = async () => {
    await fetch('/api/settings/background', { method: 'DELETE', headers: { Authorization: 'Bearer ' + tok } });
    setBg(null); setMsg('✅ Видалено');
    setTimeout(() => setMsg(''), 3000);
  };

  return (
    <>
    <Box sx={{ display:'flex', alignItems:'center', gap:2, flexWrap:'wrap' }}>
      {bg ? <img src={bg} alt="bg" style={{ width:120, height:70, objectFit:'cover', borderRadius:8, border:'1px solid #333' }}/> :
        <Box sx={{ width:120, height:70, bgcolor:'#333', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <Typography sx={{ color:'#555', fontSize:24 }}>🎨</Typography>
        </Box>}
      <Box>
        <Button variant="contained" component="label" sx={{ bgcolor:'var(--ac1, #7c4dff)', mr:1 }}>
          Завантажити<input type="file" hidden accept="image/*" onChange={upload}/>
        </Button>
        {bg && <Button variant="outlined" onClick={del} sx={{ borderColor:'#f44', color:'#f44' }}>Видалити</Button>}
        {msg && <Typography variant="caption" sx={{ color:'#4caf50', display:'block', mt:1 }}>{msg}</Typography>}
      </Box>
    </Box>
    {bg && (
      <Box sx={{ mt:2 }}>
        <Typography variant="caption" sx={{ color:'#aaa', display:'block', mb:1 }}>Розмір фону:</Typography>
        <Box sx={{ display:'flex', gap:1, mb:1, flexWrap:'wrap' }}>
          {['cover','contain','100% 100%','auto'].map(s => (
            <Button key={s} size="small" variant={bgSize===s?'contained':'outlined'}
              onClick={() => handleSize(s)}
              sx={{ fontSize:10, bgcolor:bgSize===s?'var(--ac1, #7c4dff)':'transparent', borderColor:'#555', color:bgSize===s?'#fff':'#aaa' }}>{s}</Button>
          ))}
        </Box>
        <Typography variant="caption" sx={{ color:'#aaa', display:'block', mt:1 }}>Позиція X: {bgPosX}%</Typography>
        <input type="range" min="0" max="100" value={bgPosX} onChange={e=>handleX(parseInt(e.target.value))} style={{ width:'100%', accentColor:'var(--ac1, #7c4dff)' }}/>
        <Typography variant="caption" sx={{ color:'#aaa', display:'block', mt:1 }}>Позиція Y: {bgPosY}%</Typography>
        <input type="range" min="0" max="100" value={bgPosY} onChange={e=>handleY(parseInt(e.target.value))} style={{ width:'100%', accentColor:'var(--ac1, #7c4dff)' }}/>
      </Box>
    )}
    </>
  );
}

function LogoUploader() {
  const [logo, setLogo] = React.useState(null);
  const [msg, setMsg] = React.useState('');
  const tok = localStorage.getItem('dashboard_token');

  React.useEffect(() => {
    fetch('/api/settings/logo', { headers: { Authorization: 'Bearer ' + tok } })
      .then(r => r.json()).then(d => { if (d.path) setLogo(d.path); });
  }, []);

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    const r = await fetch('/api/settings/logo', { method: 'POST', headers: { Authorization: 'Bearer ' + tok }, body: fd });
    const d = await r.json();
    setLogo(d.path + '?t=' + Date.now());
    setMsg('✅ Логотип збережено!');
    setTimeout(() => setMsg(''), 3000);
  };

  const del = async () => {
    await fetch('/api/settings/logo', { method: 'DELETE', headers: { Authorization: 'Bearer ' + tok } });
    setLogo(null); setMsg('✅ Видалено');
    setTimeout(() => setMsg(''), 3000);
  };

  return (
    <Box sx={{ display:'flex', alignItems:'center', gap:2, flexWrap:'wrap' }}>
      {logo ? <img src={logo} alt="logo" style={{ width:80, height:80, objectFit:'contain', borderRadius:8, border:'1px solid #333' }}/> :
        <Box sx={{ width:80, height:80, bgcolor:'#333', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <Typography sx={{ color:'#555', fontSize:24 }}>🖼</Typography>
        </Box>}
      <Box>
        <Button variant="contained" component="label" sx={{ bgcolor:'var(--ac1, #7c4dff)', mr:1 }}>
          Завантажити<input type="file" hidden accept="image/*" onChange={upload}/>
        </Button>
        {logo && <Button variant="outlined" onClick={del} sx={{ borderColor:'#f44', color:'#f44' }}>Видалити</Button>}
        {msg && <Typography variant="caption" sx={{ color:'#4caf50', display:'block', mt:1 }}>{msg}</Typography>}
      </Box>
    </Box>
  );
}

function StagingWorkerToggle() {
  const [enabled, setEnabled] = React.useState(true);
  const [running, setRunning] = React.useState(false);
  const [state, setState] = React.useState('unknown');
  const [pid, setPid] = React.useState(null);
  const [uptime, setUptime] = React.useState(null);
  const [updatedAt, setUpdatedAt] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const tok = localStorage.getItem('dashboard_token');

  const fetchStatus = async () => {
    try {
      const r = await fetch('/api/staging-worker/status', { headers: { Authorization: 'Bearer ' + tok } });
      const d = await r.json();
      setEnabled(d.enabled); setRunning(d.running); setState(d.state || 'unknown');
      setPid(d.pid); setUptime(d.uptime); setUpdatedAt(d.updated_at);
    } catch (e) { setMsg('❌ Помилка читання статусу: ' + e.message); }
  };

  React.useEffect(() => { fetchStatus(); }, []);

  const toggle = async (newEnabled) => {
    if (loading) return;
    if (!newEnabled && !window.confirm('Вимкнути staging worker?\n\nКластер впаде з 8 до 7 light slots. Активні tasks на staging будуть revoke.')) return;
    setLoading(true); setMsg('');
    try {
      const r = await fetch('/api/staging-worker/toggle?enabled=' + newEnabled,
        { method: 'POST', headers: { Authorization: 'Bearer ' + tok } });
      const d = await r.json();
      if (d.success) {
        setMsg(newEnabled ? '✅ Worker увімкнено' : '⏸ Worker вимкнено');
        setTimeout(() => fetchStatus(), 1500);
      } else {
        setMsg('⚠️ Помилка: ' + (d.error || 'unknown'));
      }
    } catch (e) { setMsg('❌ ' + e.message); }
    setLoading(false);
    setTimeout(() => setMsg(''), 5000);
  };

  const stateColor = state === 'RUNNING' ? '#4caf50' : (state === 'STOPPED' ? '#888' : '#ff9800');

  return (
    <Box>
      <Typography variant="body2" sx={{ color: '#aaa', mb: 2 }}>
        🟩 <b>Staging-сервер</b> має основне призначення — тестування змін перед production.
        Коли staging вільний — його можна додатково навантажити як <b>4-й Celery worker</b> (light queue: видео ≤60s).
        <br/><br/>
        ⚠️ <b>Вимикай</b> коли активно тестуєш staging features (rebuild Docker, deploy staging-bot — Celery worker може заважати).
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, p: 2, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 1 }}>
        <Typography variant="caption" sx={{ color: '#888' }}>Стан live:</Typography>
        <Chip size="small" label={state} sx={{ bgcolor: stateColor, color: '#fff', fontWeight: 'bold' }} />
        {pid && <Typography variant="caption" sx={{ color: '#aaa' }}>pid {pid}</Typography>}
        {uptime && <Typography variant="caption" sx={{ color: '#aaa' }}>uptime {uptime}</Typography>}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button
          variant={enabled ? 'contained' : 'outlined'}
          disabled={loading || enabled}
          onClick={() => toggle(true)}
          sx={{ bgcolor: enabled ? '#4caf50' : 'transparent', borderColor: '#4caf50', color: enabled ? '#fff' : '#4caf50' }}
        >
          ▶ Увімкнути Worker
        </Button>
        <Button
          variant={!enabled ? 'contained' : 'outlined'}
          disabled={loading || !enabled}
          onClick={() => toggle(false)}
          sx={{ bgcolor: !enabled ? '#888' : 'transparent', borderColor: '#888', color: !enabled ? '#fff' : '#888' }}
        >
          ⏸ Вимкнути Worker
        </Button>
        <Button size="small" onClick={fetchStatus} sx={{ color: 'var(--ac1, #7c4dff)' }}>🔄 Refresh</Button>
      </Box>

      <Typography variant="caption" sx={{ color: '#666' }}>
        Збережено у БД: <b>{enabled ? 'enabled' : 'disabled'}</b>
        {updatedAt && <span> · оновлено {updatedAt.substring(0, 16).replace('T', ' ')}</span>}
      </Typography>

      {msg && (
        <Alert severity={msg.startsWith('✅') ? 'success' : (msg.startsWith('⏸') ? 'info' : 'warning')} sx={{ mt: 2 }}>
          {msg}
        </Alert>
      )}

      <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#555', fontStyle: 'italic' }}>
        Cluster capacity: ON → 8 light slots / OFF → 7 light slots. Heavy queue (worker-1 + test) — без змін.
      </Typography>
    </Box>
  );
}

// [МЕДІА УНІКАЛІЗАТОР] D-238: картка-оплата Media через ОКРЕМУ Monobank-Банку
function MediaMonoConfig() {
  const tok = localStorage.getItem('dashboard_token');
  const [cfg, setCfg] = React.useState({ enabled: false, jar_url_eur: '', jar_url_uah: '', has_token: false });
  const [status, setStatus] = React.useState(null);
  const [msg, setMsg] = React.useState('');
  const load = () => fetch('/api/media/mono/config', { headers: { Authorization: 'Bearer ' + tok } }).then(r => r.json()).then(setCfg).catch(() => {});
  React.useEffect(() => { load(); }, []);
  const save = async (overrideEnabled) => {
    const body = { jar_url_eur: cfg.jar_url_eur, jar_url_uah: cfg.jar_url_uah, enabled: (overrideEnabled !== undefined ? overrideEnabled : cfg.enabled) };
    const r = await fetch('/api/media/mono/config', { method: 'POST', headers: { Authorization: 'Bearer ' + tok, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    const j = await r.json().catch(() => ({}));
    setMsg(r.ok ? '✅ Збережено' : '❌ ' + (j.detail || 'Помилка'));
    if (r.ok) load();
  };
  const test = async () => {
    setMsg('⏳ Перевіряю звʼязок...');
    const r = await fetch('/api/media/mono/status', { headers: { Authorization: 'Bearer ' + tok } });
    const j = await r.json().catch(() => ({}));
    setStatus(j);
    setMsg(j.has_token ? '✅ Monobank-токен є — банки нижче. Звір що твоя Media-банка у списку.' : '❌ Monobank-токен не налаштовано');
  };
  const inputSx = { mb: 1, input: { color: '#fff', fontSize: 13 }, label: { color: '#aaa' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' } };
  return (
    <>
      <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1.5 }}>
        Окрема Media-Банка (НЕ та що PL). Встав send-лінк (send.monobank.ua/jar/…), перевір звʼязок, увімкни авто. Підтвердження — поллер кожні 2 хв (нуль перетину з PL).
      </Typography>
      <TextField fullWidth size="small" label="Send-лінк банки EUR (978)" value={cfg.jar_url_eur} onChange={e => setCfg({ ...cfg, jar_url_eur: e.target.value })} placeholder="https://send.monobank.ua/jar/..." sx={inputSx}/>
      <TextField fullWidth size="small" label="Send-лінк банки UAH (980) — опц." value={cfg.jar_url_uah} onChange={e => setCfg({ ...cfg, jar_url_uah: e.target.value })} placeholder="https://send.monobank.ua/jar/..." sx={inputSx}/>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
        <Button variant="contained" onClick={() => save()} sx={{ bgcolor: 'var(--ac1, #7c4dff)' }}>💾 Зберегти</Button>
        <Button variant="outlined" onClick={test} sx={{ borderColor: '#2196f3', color: '#2196f3' }}>🔌 Перевірити звʼязок</Button>
        <Button variant={cfg.enabled ? 'contained' : 'outlined'} onClick={() => save(!cfg.enabled)}
          sx={{ bgcolor: cfg.enabled ? '#4caf50' : 'transparent', borderColor: '#4caf50', color: cfg.enabled ? '#06210a' : '#4caf50' }}>
          {cfg.enabled ? '🔄 Авто-оплата УВІМКНЕНА' : '⛔ Авто-оплата вимкнена'}
        </Button>
      </Box>
      {msg && <Typography variant="caption" sx={{ color: msg.startsWith('❌') ? '#f44336' : '#4caf50', display: 'block', mt: 1 }}>{msg}</Typography>}
      {!cfg.has_token && <Alert severity="warning" sx={{ mt: 1, bgcolor: 'rgba(255,152,0,0.1)', color: '#ff9800', fontSize: 12 }}>Monobank-токен не налаштовано (його ставить PL у своєму розділі — спільний акаунт).</Alert>}
      {status && status.jars && status.jars.length > 0 && (
        <Box sx={{ mt: 1.5, p: 1, bgcolor: 'rgba(33,150,243,0.06)', borderRadius: 1 }}>
          <Typography variant="caption" sx={{ color: '#90caf9', display: 'block', mb: 0.5 }}>🏦 Банки у твоєму Monobank ({status.jars.length}):</Typography>
          {status.jars.map((j, i) => (
            <Typography key={i} variant="caption" sx={{ color: '#ccc', display: 'block', fontSize: 11 }}>
              • {j.title} ({j.currency}) — хвіст send: <b style={{ color: '#ffd54f' }}>…{j.send_tail}</b>
            </Typography>
          ))}
          <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 0.5, fontSize: 10 }}>Хвіст лінка вище має збігатися з хвостом збереженого send-лінка.</Typography>
        </Box>
      )}
    </>
  );
}

// [PC-APP] Тариф PC Unlimited — ціна (OWNER виставляє сам, як media_mono)
function PcAppPriceConfig() {
  const tok = localStorage.getItem('dashboard_token');
  const [cfg, setCfg] = React.useState({ enabled: false, auto_pay: false });
  const [msg, setMsg] = React.useState('');
  const load = () => fetch('/api/pcapp/price', { headers: { Authorization: 'Bearer ' + tok } }).then(r => r.json()).then(setCfg).catch(() => {});
  React.useEffect(() => { load(); }, []);
  const post = async (patch) => {
    const r = await fetch('/api/pcapp/price', { method: 'POST', headers: { Authorization: 'Bearer ' + tok, 'Content-Type': 'application/json' }, body: JSON.stringify(patch) });
    setMsg(r.ok ? '✅ Збережено' : '❌ Помилка');
    if (r.ok) load();
  };
  return (
    <>
      <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1.5 }}>
        Головний вимикач продукту PC-App (Autopilot Studio). Коли ВИМКНЕНО — застосунок не пейриться і не обробляє (сервер віддає «disabled»), навіть якщо юзер оплатив. Ціни/тарифи — у розділі «Пакети / Тарифи» → фільтр «🖥 PC Unlimited».
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button variant={cfg.enabled ? 'contained' : 'outlined'} onClick={() => post({ enabled: !cfg.enabled })}
          sx={{ bgcolor: cfg.enabled ? '#4caf50' : 'transparent', borderColor: cfg.enabled ? '#4caf50' : '#f44336', color: cfg.enabled ? '#06210a' : '#f44336', fontWeight: 'bold' }}>
          {cfg.enabled ? '🟢 Продукт УВІМКНЕНО' : '🔴 Продукт ВИМКНЕНО'}
        </Button>
        <Button variant={cfg.auto_pay ? 'contained' : 'outlined'} onClick={() => post({ auto_pay: !cfg.auto_pay })}
          sx={{ bgcolor: cfg.auto_pay ? 'var(--ac1, #7c4dff)' : 'transparent', borderColor: 'var(--ac1, #7c4dff)', color: cfg.auto_pay ? '#fff' : 'var(--ac1, #7c4dff)' }}>
          {cfg.auto_pay ? '🔄 Авто-оплата Monobank УВІМКНЕНА' : '⛔ Авто-оплата вимкнена (ручна)'}
        </Button>
      </Box>
      {msg && <Typography variant="caption" sx={{ color: msg.startsWith('❌') ? '#f44336' : '#4caf50', display: 'block', mt: 1 }}>{msg}</Typography>}
    </>
  );
}

// [МЕДІА УНІКАЛІЗАТОР] D-283: платіжно-валютна панель (методи on/off + валюта фейк-юзерів + крипто-адреса + майстер-kill)
function MediaPaymentConfig() {
  const tok = localStorage.getItem('dashboard_token');
  const [cfg, setCfg] = React.useState({ payments_enabled: true, pay_show_crypto: true, pay_show_card_eur: true, pay_show_card_uah: true, pay_crypto_address: '', pay_crypto_label: 'USDT (TRC20)', display_currency: 'EUR', media_pay_show_stripe: false });
  const [monoOn, setMonoOn] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const load = () => fetch('/api/media/payment-config', { headers: { Authorization: 'Bearer ' + tok } }).then(r => r.json()).then(setCfg).catch(() => {});
  const loadMono = () => fetch('/api/media/mono/config', { headers: { Authorization: 'Bearer ' + tok } }).then(r => r.json()).then(j => setMonoOn(!!j.enabled)).catch(() => {});
  React.useEffect(() => { load(); loadMono(); }, []);
  const patch = async (body) => {
    const r = await fetch('/api/media/payment-config', { method: 'POST', headers: { Authorization: 'Bearer ' + tok, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    const j = await r.json().catch(() => ({}));
    setMsg(r.ok ? '✅ Збережено' : '❌ ' + (j.detail || 'Помилка'));
    if (r.ok) load();
  };
  const toggleMono = async () => {
    // Mono (авто-картка) керується ключем media_mono_enabled через існуючий endpoint
    const r = await fetch('/api/media/mono/config', { method: 'POST', headers: { Authorization: 'Bearer ' + tok, 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled: !monoOn }) });
    setMsg(r.ok ? '✅ Збережено' : '❌ Помилка');
    if (r.ok) loadMono();
  };
  const toggleBtn = (on, onClick, labelOn, labelOff) => (
    <Button size="small" variant={on ? 'contained' : 'outlined'} onClick={onClick}
      sx={{ minWidth: 92, bgcolor: on ? '#4caf50' : 'transparent', borderColor: on ? '#4caf50' : '#555', color: on ? '#06210a' : '#888' }}>
      {on ? (labelOn || '🟢 ON') : (labelOff || '⚪ OFF')}
    </Button>
  );
  const rowSx = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.6, borderBottom: '1px solid #222' };
  const methodRow = (key, icon, name) => (
    <Box sx={rowSx}>
      <Typography sx={{ color: '#ddd', fontSize: 14 }}>{icon} {name}</Typography>
      {toggleBtn(!!cfg[key], () => patch({ [key]: !cfg[key] }))}
    </Box>
  );
  const ccyRow = (code, icon, name) => {
    const active = cfg.display_currency === code;
    return (
      <Box sx={rowSx}>
        <Typography sx={{ color: '#ddd', fontSize: 14 }}>{icon} {name}</Typography>
        {toggleBtn(active, () => { if (!active) patch({ display_currency: code }); })}
      </Box>
    );
  };
  const inputSx = { mt: 1, input: { color: '#fff', fontSize: 13 }, label: { color: '#aaa' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' } };
  return (
    <>
      <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>
        Керує що бачить юзер на екрані оплати + у якій валюті показані (поки фейкові) юзери в Хабі. Дрімний дефолт — усе ON. Крипто-онлі: лиши USDT, решту вимкни.
      </Typography>
      <Typography variant="subtitle2" sx={{ color: '#ff9800', mt: 1 }}>Методи оплати <span style={{ color: '#666', fontSize: 11 }}>(можна декілька одночасно)</span></Typography>
      <Box sx={{ mb: 1 }}>
        {methodRow('pay_show_crypto', '₮', cfg.pay_crypto_label || 'USDT (TRC20)')}
        {methodRow('pay_show_card_eur', '💳', 'Картка EUR')}
        {methodRow('pay_show_card_uah', '💳', 'Картка UAH')}
        <Box sx={rowSx}>
          <Typography sx={{ color: '#ddd', fontSize: 14 }}>⚡ Mono (авто-картка)</Typography>
          {toggleBtn(monoOn, toggleMono)}
        </Box>
        {methodRow('media_pay_show_stripe', '🅢', 'Stripe (MediaSystemGroup, спільний акаунт)')}
      </Box>
      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>🅢 Stripe — окремий незалежний спосіб через спільний Stripe-акаунт (той самий, що й у NUMERIQ/Хіромантії/ZODIAQ). Не чіпає mono/крипто/картку.</Typography>
      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>⚡ Mono — це кнопка «Оплатити карткою (авто)». Send-лінки банки налаштовуються у блоці «💳 Картка-оплата Media» нижче.</Typography>
      <Typography variant="subtitle2" sx={{ color: '#ff9800', mt: 1 }}>Валюта у фейк-юзерах <span style={{ color: '#666', fontSize: 11 }}>(тільки одна активна)</span></Typography>
      <Box sx={{ mb: 1 }}>
        {ccyRow('EUR', '€', 'EUR')}
        {ccyRow('USD', '$', 'USD')}
        {ccyRow('USDT', '₮', 'USDT')}
        {ccyRow('MASK', '*', 'Маска (***)')}
      </Box>
      <TextField fullWidth size="small" label="₮ USDT TRC20-адреса (отримання)" value={cfg.pay_crypto_address} onChange={e => setCfg({ ...cfg, pay_crypto_address: e.target.value })} onBlur={() => patch({ pay_crypto_address: cfg.pay_crypto_address })} placeholder="T..." sx={inputSx}/>
      <TextField fullWidth size="small" label="Підпис крипто-методу" value={cfg.pay_crypto_label} onChange={e => setCfg({ ...cfg, pay_crypto_label: e.target.value })} onBlur={() => patch({ pay_crypto_label: cfg.pay_crypto_label })} placeholder="USDT (TRC20)" sx={inputSx}/>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5, p: 1, bgcolor: cfg.payments_enabled ? 'rgba(76,175,80,0.08)' : 'rgba(244,67,54,0.08)', borderRadius: 1 }}>
        <Typography sx={{ color: cfg.payments_enabled ? '#4caf50' : '#f44336', fontSize: 13, fontWeight: 600 }}>
          {cfg.payments_enabled ? '🟢 Платежі увімкнено' : '🔴 Платежі ВИМКНЕНО (майстер-kill)'}
        </Typography>
        {toggleBtn(cfg.payments_enabled, () => patch({ payments_enabled: !cfg.payments_enabled }), '🟢 ON', '🔴 OFF')}
      </Box>
      <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 0.5 }}>Майстер-вимикач: OFF → екран оплати показує «тимчасово недоступно» + кнопка підтримки.</Typography>
      {msg && <Typography variant="caption" sx={{ color: msg.startsWith('❌') ? '#f44336' : '#4caf50', display: 'block', mt: 1 }}>{msg}</Typography>}
    </>
  );
}


export default function SettingsPage() {
  const [days, setDays] = useState('3');
  const [msg, setMsg] = useState('');
  const [broadcastText, setBroadcastText] = useState('');
  const [broadcastFile, setBroadcastFile] = useState(null);
  const [broadcasts, setBroadcasts] = useState([]);
  const [viewBroadcast, setViewBroadcast] = useState(null);
  const [broadcastMediaType, setBroadcastMediaType] = useState('photo');
  const [broadcastResult, setBroadcastResult] = useState(null);
  const [broadcasting, setBroadcasting] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);
  const [keyValues, setKeyValues] = useState({});
  const [keyMsg, setKeyMsg] = useState({});
  const [botTokens, setBotTokens] = useState([]);
  const [botTokenValues, setBotTokenValues] = useState({});
  const [botTokenMsg, setBotTokenMsg] = useState({});
  // [МЕДІА УНІКАЛІЗАТОР] D-221 C: порядок блоків (перетягування, як картки Статистики)
  const SET_ORDER_KEY = 'settings_block_order';
  const [blockOrder, setBlockOrder] = useState(() => { try { return JSON.parse(localStorage.getItem(SET_ORDER_KEY)); } catch { return null; } });
  const onBlockDrop = (list, from, to) => { const o = [...list]; const [m] = o.splice(from, 1); o.splice(to, 0, m); setBlockOrder(o); localStorage.setItem(SET_ORDER_KEY, JSON.stringify(o)); };

  useEffect(() => {
    getApiKeys().then(r => {
      setApiKeys(r.data);
      const vals = {};
      r.data.forEach(k => { vals[k.service] = ''; });
      setKeyValues(vals);
    });
  }, []);

  const fetchBotTokens = () => {
    const tok = localStorage.getItem('dashboard_token');
    fetch('/api/settings/bot-tokens', { headers: { Authorization: 'Bearer ' + tok } })
      .then(r => r.json()).then(d => { if (Array.isArray(d)) setBotTokens(d); })
      .catch(() => {});
  };
  useEffect(() => { fetchBotTokens(); }, []);

  // [D-221 C] founder/promo конфіг → перенесено у Статистику (StatsPage), тут більше не рендериться

  const handleSaveKey = async (service) => {
    const key = keyValues[service];
    if (!key) return;
    try {
      if (service === 'telegram_bot') {
        // Спеціальна перевірка для bot token
        const tok = localStorage.getItem('dashboard_token');
        const r = await fetch('/api/settings/bot-token?token='+encodeURIComponent(key),
          { method:'POST', headers:{ Authorization:'Bearer '+tok } });
        const d = await r.json();
        if (d.ok) {
          setKeyMsg({ ...keyMsg, [service]: d.message });
        } else {
          setKeyMsg({ ...keyMsg, [service]: '❌ ' + (d.detail || 'Помилка') });
        }
      } else {
        await saveApiKey(service, key);
        setKeyMsg({ ...keyMsg, [service]: '✅ Збережено!' });
      }
      setKeyValues({ ...keyValues, [service]: '' });
      getApiKeys().then(r => setApiKeys(r.data));
      setTimeout(() => setKeyMsg({ ...keyMsg, [service]: '' }), 3000);
    } catch { setKeyMsg({ ...keyMsg, [service]: '❌ Помилка' }); }
  };

  const handleSaveBotToken = async (bot_key) => {
    const token = botTokenValues[bot_key];
    if (!token) return;
    setBotTokenMsg({ ...botTokenMsg, [bot_key]: '⏳ Перевірка та деплой...' });
    try {
      const auth = localStorage.getItem('dashboard_token');
      const r = await fetch('/api/settings/bot-tokens/' + bot_key + '?token=' + encodeURIComponent(token),
        { method: 'POST', headers: { Authorization: 'Bearer ' + auth } });
      const d = await r.json();
      if (r.ok && d.ok) {
        const dep = d.deploy_status === 'ok' ? '✅ Збережено + Deploy OK' : ('✅ Збережено (deploy: ' + d.deploy_status + ')');
        setBotTokenMsg({ ...botTokenMsg, [bot_key]: dep });
        setBotTokenValues({ ...botTokenValues, [bot_key]: '' });
        fetchBotTokens();
        setTimeout(() => setBotTokenMsg({ ...botTokenMsg, [bot_key]: '' }), 5000);
      } else {
        setBotTokenMsg({ ...botTokenMsg, [bot_key]: '❌ ' + (d.detail || d.message || 'Помилка') });
      }
    } catch (e) { setBotTokenMsg({ ...botTokenMsg, [bot_key]: '❌ Помилка: ' + e.message }); }
  };
  const [error, setError] = useState('');

  const [newUser, setNewUser] = useState('');
  const [newPass, setNewPass] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [credMsg, setCredMsg] = useState('');
  const [credError, setCredError] = useState('');

  React.useEffect(() => {
    const tok = localStorage.getItem('dashboard_token');
    fetch('/api/broadcasts',{headers:{Authorization:'Bearer '+tok}}).then(r=>r.json()).then(setBroadcasts).catch(()=>{});
  }, []);

  const deleteOneBroadcast = async (bid, tid, mid) => {
    const tok = localStorage.getItem('dashboard_token');
    await fetch('/api/broadcasts/'+bid+'/user/'+tid+'?message_id='+mid,{method:'DELETE',headers:{Authorization:'Bearer '+tok}});
    setBroadcasts(b => b.map(x => x.broadcast_id===bid ? {...x, users: x.users.filter(u=>u.telegram_id!==tid)} : x).filter(x=>x.users?.length>0));
  };

  const deleteBroadcast = async (bid) => {
    if (!window.confirm('Видалити цю розсилку у всіх юзерів?')) return;
    const tok = localStorage.getItem('dashboard_token');
    await fetch('/api/broadcasts/'+bid,{method:'DELETE',headers:{Authorization:'Bearer '+tok}});
    setBroadcasts(b => b.filter(x => x.broadcast_id !== bid));
  };

  const handleBroadcast = async () => {
    if (!broadcastText.trim() && !broadcastFile) return;
    setBroadcasting(true);
    try {
      const tok = localStorage.getItem('dashboard_token');
      let r;
      if (broadcastFile) {
        const fd = new FormData();
        fd.append('message', broadcastText);
        fd.append('file', broadcastFile);
        fd.append('media_type', broadcastMediaType);
        r = await fetch('/api/broadcast?message=' + encodeURIComponent(broadcastText) + '&media_type=' + broadcastMediaType, {
          method: 'POST', headers: { Authorization: 'Bearer ' + tok }, body: fd
        });
      } else {
        r = await fetch('/api/broadcast?message=' + encodeURIComponent(broadcastText), {
          method: 'POST', headers: { Authorization: 'Bearer ' + tok, 'Content-Type': 'application/json' }
        });
      }
      const d = await r.json();
      setBroadcastResult(d);
      setBroadcastText(''); setBroadcastFile(null);
      fetch('/api/broadcasts',{headers:{Authorization:'Bearer '+tok}}).then(r=>r.json()).then(setBroadcasts);
    } catch(e) { setBroadcastResult({error: 'Помилка'}); }
    setBroadcasting(false);
  };

  const handleUpdateTrial = async () => {
    try {
      const res = await updateTrialAll(parseInt(days));
      setMsg('Оновлено ' + res.data.affected + ' юзерів на ' + days + ' днів');
      setError('');
    } catch { setError('Помилка оновлення'); setMsg(''); }
  };

  const handleChangeCredentials = async () => {
    if (!newUser || !newPass) { setCredError('Заповни всі поля'); return; }
    if (newPass !== confirmPass) { setCredError('Паролі не співпадають'); return; }
    try {
      const token = localStorage.getItem('dashboard_token');
      await changeCredentials(newUser, newPass, token);
      setCredMsg('Дані входу оновлено! Увійди знову.');
      setCredError('');
      setTimeout(() => {
        localStorage.removeItem('dashboard_token');
        window.location.reload();
      }, 2000);
    } catch { setCredError('Помилка оновлення'); setCredMsg(''); }
  };

  // [МЕДІА УНІКАЛІЗАТОР] D-221 C: компактні перетягувані блоки (drag-ручка зверху; інпути не заважають). w:6=пів,12=повний.
  const blocks = [
    { id: 'trial', w: 6, c: 'var(--ac1, #7c4dff)', node: (<>
      <Typography variant="subtitle1" sx={{ color: 'var(--ac1, #7c4dff)', mb: 2 }}>⏳ Trial для всіх юзерів</Typography>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
        <TextField type="number" label="Кількість днів" size="small" value={days} onChange={e => setDays(e.target.value)} sx={{ width: 150, input: { color: '#fff' }, label: { color: '#aaa' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' } }}/>
        <Button variant="contained" onClick={handleUpdateTrial} sx={{ bgcolor: 'var(--ac1, #7c4dff)' }}>Оновити всім</Button>
      </Box>
      {msg && <Alert severity="success" sx={{ bgcolor: 'rgba(76,175,80,0.1)', color: '#4caf50' }}>{msg}</Alert>}
      {error && <Alert severity="error" sx={{ bgcolor: 'rgba(244,67,54,0.1)', color: '#f44336' }}>{error}</Alert>}
      <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 1 }}>Оновлює trial_end для всіх юзерів зі статусом trial</Typography>
    </>) },
    { id: 'creds', w: 6, c: 'var(--ac1, #7c4dff)', node: (<>
      <Typography variant="subtitle1" sx={{ color: 'var(--ac1, #7c4dff)', mb: 2 }}>🔐 Змінити дані входу</Typography>
      <TextField fullWidth label="Новий логін" size="small" value={newUser} onChange={e => setNewUser(e.target.value)} sx={{ mb: 2, input: { color: '#fff' }, label: { color: '#aaa' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' } }}/>
      <TextField fullWidth label="Новий пароль" type="password" size="small" value={newPass} onChange={e => setNewPass(e.target.value)} sx={{ mb: 2, input: { color: '#fff' }, label: { color: '#aaa' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' } }}/>
      <TextField fullWidth label="Підтвердити пароль" type="password" size="small" value={confirmPass} onChange={e => setConfirmPass(e.target.value)} sx={{ mb: 2, input: { color: '#fff' }, label: { color: '#aaa' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' } }}/>
      <Button fullWidth variant="contained" onClick={handleChangeCredentials} sx={{ bgcolor: 'var(--ac1, #7c4dff)', '&:hover': { bgcolor: '#6c3dff' } }}>Зберегти</Button>
      {credMsg && <Alert severity="success" sx={{ mt: 2, bgcolor: 'rgba(76,175,80,0.1)', color: '#4caf50' }}>{credMsg}</Alert>}
      {credError && <Alert severity="error" sx={{ mt: 2, bgcolor: 'rgba(244,67,54,0.1)', color: '#f44336' }}>{credError}</Alert>}
    </>) },
    { id: 'uniq', w: 6, c: '#4caf50', node: (<><Typography variant="h6" sx={{ color: '#4caf50', mb: 2 }}>🔧 Блок унікалізації</Typography><ShowUniqInfo/><MicroCutsToggle/></>) },
    { id: 'twofa', w: 6, c: 'var(--ac1, #7c4dff)', node: (<><Typography variant="h6" sx={{ color: 'var(--ac1, #7c4dff)', mb: 2 }}>🔐 Двофакторна автентифікація (2FA)</Typography><TwoFactorSetup/></>) },
    { id: 'maxver', w: 6, c: '#ff9800', node: (<><Typography variant="h6" sx={{ color: '#ff9800', mb: 2 }}>🎬 Ліміт версій за раз</Typography><MaxPerSession/></>) },
    { id: 'filelimits', w: 6, c: '#ff9800', node: (<><Typography variant="h6" sx={{ color: '#ff9800', mb: 2 }}>📦 Ліміти файлів — основне відео</Typography><FileLimits/></>) },
    { id: 'auxlimits', w: 6, c: '#ff9800', node: (<><Typography variant="h6" sx={{ color: '#ff9800', mb: 2 }}>📦 Ліміти файлів — Реклама/Хромакеї</Typography><AuxFileLimits/></>) },
    { id: 'logo', w: 6, c: 'var(--ac1, #7c4dff)', node: (<><Typography variant="h6" sx={{ color: 'var(--ac1, #7c4dff)', mb: 2 }}>🖼 Логотип</Typography><LogoUploader/></>) },
    { id: 'bg', w: 6, c: 'var(--ac1, #7c4dff)', node: (<><Typography variant="h6" sx={{ color: 'var(--ac1, #7c4dff)', mb: 2 }}>🎨 Фон</Typography><BgUploader/></>) },
    { id: 'theme', w: 6, c: 'var(--ac1, #7c4dff)', node: (<><Typography variant="h6" sx={{ color: 'var(--ac1, #7c4dff)', mb: 2 }}>🎨 Тема та вигляд</Typography><ThemeSettings/></>) },
    { id: 'staging', w: 6, c: '#4caf50', node: (<><Typography variant="h6" sx={{ color: '#4caf50', mb: 2 }}>🟩 Staging-сервер як Worker-3</Typography><StagingWorkerToggle/></>) },
    { id: 'paymentcurrency', w: 12, c: '#ff9800', node: (<><Typography variant="h6" sx={{ color: '#ff9800', mb: 1 }}>💳 Платежі та валюти</Typography><MediaPaymentConfig/></>) },
    { id: 'mediamono', w: 12, c: '#4caf50', node: (<><Typography variant="h6" sx={{ color: '#4caf50', mb: 1 }}>💳 Картка-оплата Media (Monobank-Банка)</Typography><MediaMonoConfig/></>) },
    { id: 'pcappprice', w: 12, c: '#00bcd4', node: (<><Typography variant="h6" sx={{ color: '#00bcd4', mb: 1 }}>🖥 PC-App (Autopilot Studio) — керування</Typography><PcAppPriceConfig/></>) },
    { id: 'apikeys', w: 12, c: 'var(--ac1, #7c4dff)', node: (<>
      <Typography variant="h6" sx={{ color: 'var(--ac1, #7c4dff)', mb: 2 }}>🔑 API Ключі</Typography>
      {[
        { service: 'openai', label: '🤖 OpenAI', placeholder: 'sk-...' },
        { service: 'anthropic', label: '🧠 Anthropic (Claude)', placeholder: 'sk-ant-...' },
        { service: 'gemini', label: '✨ Google Gemini (free)', placeholder: 'AIza...' },
      ].map(({ service, label, placeholder }) => {
        const current = apiKeys.find(k => k.service === service);
        return (
          <Box key={service} sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ color: '#aaa', mb: 0.5 }}>{label}</Typography>
            {current?.masked && <Typography variant="caption" sx={{ color: '#555', display: 'block', mb: 0.5 }}>Поточний: {current.masked}</Typography>}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField size="small" placeholder={placeholder} type="password" value={keyValues[service] || ''} onChange={e => setKeyValues({ ...keyValues, [service]: e.target.value })} sx={{ flexGrow: 1, input: { color: '#fff' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' } }}/>
              <Button variant="contained" onClick={() => handleSaveKey(service)} sx={{ bgcolor: 'var(--ac1, #7c4dff)' }}>Зберегти</Button>
            </Box>
            {keyMsg[service] && <Typography variant="caption" sx={{ color: '#4caf50' }}>{keyMsg[service]}</Typography>}
          </Box>
        );
      })}
    </>) },
    { id: 'bots', w: 12, c: '#4caf50', node: (<>
      <Typography variant="h6" sx={{ color: '#4caf50', mb: 2 }}>🤖 Telegram Боти</Typography>
      <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 2 }}>Ротація токенів з валідацією через getMe + auto-deploy на цільовий сервер.</Typography>
      {botTokens.length === 0 && <Typography variant="caption" sx={{ color: '#666' }}>Завантаження...</Typography>}
      {botTokens.map(b => (
        <Box key={b.bot_key} sx={{ mb: 2, p: 2, border: '1px solid #2a2a3e', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ color: '#fff', mb: 0.5 }}>🤖 @{b.username}</Typography>
          <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>{b.server_emoji} {b.server_name}{b.deploy_path ? ' → ' + b.deploy_path : ''}</Typography>
          {b.has_token ? (
            <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>Поточний: {b.masked} · оновлено {b.updated_at ? new Date(b.updated_at).toLocaleString('uk-UA') : '—'}</Typography>
          ) : (
            <Typography variant="caption" sx={{ color: '#ff9800', display: 'block', mb: 0.5 }}>⚠️ Token не збережено</Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <TextField size="small" placeholder="Новий token..." type="password" value={botTokenValues[b.bot_key] || ''} onChange={e => setBotTokenValues({ ...botTokenValues, [b.bot_key]: e.target.value })} sx={{ flexGrow: 1, input: { color: '#fff' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' } }}/>
            <Button variant="contained" onClick={() => handleSaveBotToken(b.bot_key)} sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#388e3c' } }}>Зберегти</Button>
          </Box>
          {botTokenMsg[b.bot_key] && (
            <Typography variant="caption" sx={{ color: botTokenMsg[b.bot_key].startsWith('❌') ? '#f44336' : (botTokenMsg[b.bot_key].startsWith('⏳') ? '#ff9800' : '#4caf50'), display: 'block', mt: 0.5 }}>{botTokenMsg[b.bot_key]}</Typography>
          )}
        </Box>
      ))}
    </>) },
  ];
  const _defIds = blocks.map(b => b.id);
  const _ordIds = (Array.isArray(blockOrder) ? blockOrder.filter(id => _defIds.includes(id)).concat(_defIds.filter(id => !blockOrder.includes(id))) : _defIds);

  return (
    <Box>
      <Typography variant="h5" sx={{ color: '#fff', mb: 0.5, fontWeight: 'bold' }}>⚙️ Налаштування</Typography>
      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 2 }}>↕️ Тягни за смужку «⠿ перетягнути» зверху блоку, щоб змінити порядок (зберігається у браузері)</Typography>
      <Grid container spacing={2} alignItems="flex-start">
        {_ordIds.map((id, i) => {
          const b = blocks.find(x => x.id === id);
          if (!b) return null;
          return (
            <Grid item xs={12} md={b.w} key={id} onDragOver={e => e.preventDefault()} onDrop={() => { if (window._setDragFrom !== undefined) { onBlockDrop(_ordIds, window._setDragFrom, i); window._setDragFrom = undefined; } }}>
              <Paper sx={{ bgcolor: '#1a1a2e', border: '1px solid ' + (b.c || '#333'), borderRadius: 1, height: '100%', overflow: 'hidden' }}>
                <Box draggable onDragStart={() => { window._setDragFrom = i; }} sx={{ cursor: 'grab', px: 1.5, py: 0.5, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(255,255,255,0.04)', borderBottom: '1px solid #2a2a3e', '&:active': { cursor: 'grabbing' } }}>
                  <span style={{ color: '#555', fontSize: 16, lineHeight: 1 }}>⠿</span>
                  <Typography variant="caption" sx={{ color: '#555' }}>перетягнути</Typography>
                </Box>
                <Box sx={{ p: 3 }}>{b.node}</Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
      {/* Розсилка — повна ширина, нижче сітки (не переноситься) */}
    <Paper sx={{ bgcolor: '#1a1a2e', border: '1px solid #f44336', p: 3, mt: 3 }}>
        <Typography variant="h6" sx={{ color: '#f44336', mb: 2 }}>📢 Розсилка всім юзерам</Typography>
        <TextField fullWidth multiline rows={3} placeholder="Текст повідомлення (підтримує HTML)..."
          value={broadcastText} onChange={e => setBroadcastText(e.target.value)}
          sx={{ mb: 2, textarea:{color:'#fff'}, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#555'} }}/>
        <Box sx={{ display:'flex', gap:1, mb:1, alignItems:'center', flexWrap:'wrap' }}>
          <Button variant="outlined" component="label" size="small" sx={{ borderColor:'#555', color:'#aaa' }}>
            📎 Фото/Відео<input type="file" hidden accept="image/*,video/*,.heic,.heif,.mov,.avi,.mkv,.webm" onChange={e=>{setBroadcastFile(e.target.files[0]); setBroadcastMediaType(e.target.files[0]?.type.startsWith('video')?'video':'photo');}}/>
          </Button>
          {broadcastFile && <Typography variant="caption" sx={{ color:'#4caf50' }}>✅ {broadcastFile.name}</Typography>}
          {broadcastFile && <Button size="small" onClick={()=>setBroadcastFile(null)} sx={{ color:'#f44', minWidth:0 }}>✕</Button>}
        </Box>
        <Box sx={{ display:'flex', gap:2, alignItems:'center' }}>
          <Button variant="contained" onClick={handleBroadcast} disabled={broadcasting || (!broadcastText.trim() && !broadcastFile)}
            sx={{ bgcolor:'#f44336', '&:hover':{bgcolor:'#d32f2f'} }}>
            {broadcasting ? '⏳ Надсилається...' : '📤 Надіслати всім'}
          </Button>
          {broadcastResult && !broadcastResult.error && (
            <Typography sx={{ color:'#4caf50' }}>✅ Надіслано: {broadcastResult.sent} | Помилок: {broadcastResult.failed}</Typography>
          )}
          {broadcastResult?.error && <Typography sx={{ color:'#f44336' }}>❌ {broadcastResult.error}</Typography>}
        </Box>
        {broadcasts.length > 0 && (
          <Box sx={{ mt:2 }}>
            <Typography variant="subtitle2" sx={{ color:'#aaa', mb:1 }}>📋 Відправлені розсилки:</Typography>
            {broadcasts.map(b => (
              <Box key={b.broadcast_id} sx={{ mb:1, border:'1px solid #333', borderRadius:1, p:1 }}>
                <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <Typography variant="caption" sx={{ color:'#888', cursor:'pointer' }} onClick={() => setViewBroadcast(viewBroadcast===b.broadcast_id ? null : b.broadcast_id)}>
                    #{b.broadcast_id} · {b.users?.length} юзерів · {b.sent_at?.substring(0,16)} 👁
                  </Typography>
                  <Button size="small" onClick={() => deleteBroadcast(b.broadcast_id)} sx={{ color:'#f44', fontSize:11 }}>🗑 Всім</Button>
                </Box>
                {viewBroadcast===b.broadcast_id && (
                  <Box sx={{ mt:0.5, p:1, bgcolor:'rgba(255,255,255,0.05)', borderRadius:1 }}>
                    {b.users?.[0]?.message_text && <Typography variant="caption" sx={{ color:'#aaa', display:'block', mb:0.5 }}>📝 {b.users[0].message_text}</Typography>}
                    {b.users?.[0]?.media_type === 'photo' && b.users?.[0]?.media_ext && <img src={'/media/broadcasts/'+b.broadcast_id+'.'+b.users[0].media_ext} alt="broadcast" style={{ maxWidth:200, maxHeight:200, borderRadius:4 }}/>}
                    {b.users?.[0]?.media_type === 'video' && b.users?.[0]?.media_ext && <video src={'/media/broadcasts/'+b.broadcast_id+'.'+b.users[0].media_ext} controls style={{ maxWidth:250, maxHeight:200 }}/>}
                    {!b.users?.[0]?.media_type && !b.users?.[0]?.message_text && <Typography variant="caption" sx={{ color:'#555' }}>Тільки медіа без тексту</Typography>}
                  </Box>
                )}
                <Box sx={{ display:'flex', flexWrap:'wrap', gap:0.5, mt:0.5 }}>
                  {b.users?.map(u => (
                    <Chip key={u.telegram_id} size="small"
                      label={'@'+(u.telegram_username||u.telegram_id)}
                      onDelete={() => deleteOneBroadcast(b.broadcast_id, u.telegram_id, u.message_id)}
                      sx={{ bgcolor:'rgba(var(--ac1rgb, 124, 77, 255),0.1)', color:'#aaa', fontSize:10 }}/>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
}
